import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { ArrowRight, RefreshCw, AlertTriangle, Clock, User, Lock, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal "Trocar Operação" — permite ao gestor substituir a operação de um registo
 * (em curso ou terminado) por outra operação do mesmo lote.
 *
 * Operações agrupadas por categoria com a cor real da categoria. A categoria da
 * operação actual aparece primeiro.
 *
 * Se a operação seleccionada já tem peças produzidas, mostra um alerta de aviso
 * (a contagem actual seria somada à existente — risco de sobreposição).
 */
export default function TrocarOperacaoModal({ open, onOpenChange, registoId, registoInfo, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({ items: [], operacao_atual: null });
  const [novaOpId, setNovaOpId] = useState('');
  const [aceitouSobrescrever, setAceitouSobrescrever] = useState(false);

  useEffect(() => {
    if (!open || !registoId) {
      setNovaOpId('');
      setAceitouSobrescrever(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${API}/registos-tempo/${registoId}/operacoes-elegiveis`)
      .then((res) => setData(res.data || { items: [], operacao_atual: null }))
      .catch((e) => {
        toast.error('Erro ao carregar operações: ' + (e.response?.data?.detail || e.message));
      })
      .finally(() => setLoading(false));
  }, [open, registoId]);

  // Sempre que muda a operação alvo, resetar a aceitação de sobrescrever.
  useEffect(() => {
    setAceitouSobrescrever(false);
  }, [novaOpId]);

  const novaOp = useMemo(
    () => data.items.find((o) => o.operacao_id === novaOpId) || null,
    [data.items, novaOpId]
  );

  const opAtual = data.operacao_atual || {};
  // Tempo standard vem do backend em SEGUNDOS (tempo_ajustado da OF ou tempo_standard do catálogo).
  // Formatar dinamicamente: <60s mostra segundos, <3600s mostra min[+s], ≥3600s mostra h+min.
  const fmtTempo = (s) => {
    const n = Number(s);
    if (!n || n <= 0) return '— ';
    if (n < 60) return `${n}s`;
    const h = Math.floor(n / 3600);
    const m = Math.floor((n % 3600) / 60);
    const sec = n % 60;
    if (h > 0) return m > 0 ? `${h}h ${m}min` : `${h}h`;
    return sec > 0 ? `${m}min ${sec}s` : `${m}min`;
  };

  // Operação alvo já tem peças? (cenário que requer atenção)
  const novaOpTemPecas = novaOp?.quantidade_ja_produzida > 0;

  // Agrupar operações por categoria. A categoria da operação actual aparece primeiro.
  const gruposCategoria = useMemo(() => {
    const map = new Map();
    data.items.forEach((op) => {
      const key = op.categoria_id || '__sem__';
      if (!map.has(key)) {
        map.set(key, {
          categoria_id: key,
          categoria_nome: op.categoria_nome || 'Sem categoria',
          categoria_cor: op.categoria_cor || '#94a3b8',
          items: [],
        });
      }
      map.get(key).items.push(op);
    });
    const arr = [...map.values()];
    const catAtualId = opAtual.categoria_id || '__sem__';
    arr.sort((a, b) => {
      if (a.categoria_id === catAtualId) return -1;
      if (b.categoria_id === catAtualId) return 1;
      return a.categoria_nome.localeCompare(b.categoria_nome);
    });
    return arr;
  }, [data.items, opAtual.categoria_id]);

  const handleConfirmar = async () => {
    if (!novaOpId) {
      toast.error('Selecciona uma operação alvo');
      return;
    }
    if (novaOpTemPecas && !aceitouSobrescrever) {
      toast.warning('A operação alvo já tem peças produzidas. Confirme abaixo para prosseguir.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.patch(`${API}/registos-tempo/${registoId}/trocar-operacao`, {
        nova_operacao_id: novaOpId,
        confirmar_sobrescrever: novaOpTemPecas && aceitouSobrescrever,
      });
      toast.success(`Operação trocada para ${novaOp?.operacao_codigo} · ${novaOp?.operacao_nome}`);
      if (onSuccess) onSuccess(res.data?.registo);
      onOpenChange(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (typeof detail === 'object' && detail?.code === 'OP_ALVO_TEM_PECAS') {
        toast.warning(`Esta operação já tem ${detail.quantidade_ja_produzida} peças. Confirme abaixo para sobrescrever.`);
      } else {
        toast.error('Erro ao trocar: ' + (typeof detail === 'string' ? detail : (detail?.message || e.message)));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="trocar-operacao-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-amber-600" />
            Trocar Operação do Registo
          </DialogTitle>
          <p className="text-xs text-stone-500 mt-1">
            A operação será substituída no registo do operador e nos relatórios. Não recomeça o tempo.
            {opAtual.ativo ? ' (Registo em curso — o operador receberá um alerta na App)' : ' (Registo terminado)'}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center text-stone-400 text-sm">A carregar operações...</div>
        ) : (
          <div className="space-y-4">
            {/* Contexto do registo */}
            {registoInfo && (
              <div className="rounded-lg bg-stone-50 border p-3 text-xs flex flex-wrap items-center gap-3">
                <span className="flex items-center gap-1 text-stone-700">
                  <User className="h-3 w-3" /> <strong>{registoInfo.operador_nome || '—'}</strong>
                </span>
                {registoInfo.lote_codigo && (
                  <span className="text-stone-700">Lote: <strong>{registoInfo.lote_codigo}</strong></span>
                )}
                {registoInfo.of_numero && (
                  <span className="text-stone-700">OF: <strong>{registoInfo.of_numero}</strong></span>
                )}
                {opAtual.ativo && (
                  <span className="ml-auto px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-bold text-[10px]">EM CURSO</span>
                )}
              </div>
            )}

            {/* Operação ATUAL → NOVA */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-center">
              {/* Atual */}
              <div
                className="rounded-lg border-l-4 border-2 p-3"
                style={{
                  borderLeftColor: opAtual.categoria_cor || '#fb7185',
                  borderColor: '#fecdd3',
                  background: '#fff1f2',
                }}
              >
                <p className="text-[10px] uppercase font-bold mb-1 tracking-wider" style={{ color: opAtual.categoria_cor || '#be123c' }}>
                  Operação Actual
                </p>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span
                    className="inline-block px-2 py-0.5 rounded font-bold text-sm text-white"
                    style={{ background: opAtual.categoria_cor || '#be123c' }}
                  >
                    {opAtual.operacao_codigo || '—'}
                  </span>
                  <span className="text-sm font-semibold text-stone-700 truncate" title={opAtual.operacao_nome}>{opAtual.operacao_nome}</span>
                </div>
                {opAtual.categoria_nome && (
                  <p className="mt-1 text-[10px] font-semibold" style={{ color: opAtual.categoria_cor || '#94a3b8' }}>{opAtual.categoria_nome}</p>
                )}
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <p className="text-stone-400">Tempo/peça</p>
                    <p className="font-bold text-stone-700">{fmtTempo(opAtual.tempo_standard)}</p>
                  </div>
                  <div>
                    <p className="text-stone-400">Peças</p>
                    <p className="font-bold text-stone-700">{opAtual.quantidade_produzida ?? '—'}</p>
                  </div>
                </div>
              </div>

              <ArrowRight className="h-6 w-6 text-stone-400 mx-auto hidden md:block" />

              {/* Nova */}
              <div
                className={`rounded-lg border-l-4 border-2 p-3 transition ${novaOp ? 'shadow-sm' : ''}`}
                style={
                  novaOp
                    ? {
                        borderLeftColor: novaOp.categoria_cor || '#10b981',
                        borderColor: '#a7f3d0',
                        background: '#ecfdf5',
                      }
                    : { borderColor: '#e2e8f0', background: '#f8fafc', borderLeftColor: '#cbd5e1' }
                }
              >
                <p className="text-[10px] uppercase font-bold mb-1 tracking-wider" style={{ color: novaOp ? (novaOp.categoria_cor || '#047857') : '#94a3b8' }}>
                  Nova Operação
                </p>
                {novaOp ? (
                  <>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span
                        className="inline-block px-2 py-0.5 rounded font-bold text-sm text-white"
                        style={{ background: novaOp.categoria_cor || '#047857' }}
                      >
                        {novaOp.operacao_codigo}
                      </span>
                      <span className="text-sm font-semibold text-stone-700 truncate" title={novaOp.operacao_nome}>{novaOp.operacao_nome}</span>
                    </div>
                    {novaOp.categoria_nome && (
                      <p className="mt-1 text-[10px] font-semibold" style={{ color: novaOp.categoria_cor || '#94a3b8' }}>{novaOp.categoria_nome}</p>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-stone-400">Tempo/peça</p>
                        <p className="font-bold text-emerald-700">{fmtTempo(novaOp.tempo_standard)}</p>
                      </div>
                      <div>
                        <p className="text-stone-400">Já produzidas</p>
                        <p className={`font-bold ${novaOpTemPecas ? 'text-amber-700' : 'text-stone-700'}`}>{novaOp.quantidade_ja_produzida}</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-xs text-stone-400 italic mt-2">Selecciona abaixo →</p>
                )}
              </div>
            </div>

            {/* Aviso quando nova op já tem peças */}
            {novaOpTemPecas && (
              <div className="rounded-lg bg-amber-50 border-2 border-amber-300 p-3 text-xs space-y-2">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-bold text-amber-900 mb-1">
                      Atenção: a operação alvo já tem {novaOp.quantidade_ja_produzida} peça(s) contabilizadas
                    </p>
                    <p className="text-amber-800 leading-snug">
                      Se prosseguir, as <strong>{opAtual.quantidade_produzida || 0} peça(s)</strong> deste registo serão <strong>somadas</strong> às existentes
                      (total ficará {(novaOp.quantidade_ja_produzida || 0) + (opAtual.quantidade_produzida || 0)} peças nessa operação).
                    </p>
                    <p className="mt-1 text-amber-700">
                      Em alternativa, considere primeiro <strong>mover essa contagem</strong> da operação alvo para uma operação ainda por iniciar, e só depois fazer esta troca.
                    </p>
                  </div>
                </div>
                <label className="flex items-start gap-2 cursor-pointer pl-7" data-testid="trocar-confirmar-sobrescrever">
                  <input
                    type="checkbox"
                    checked={aceitouSobrescrever}
                    onChange={(e) => setAceitouSobrescrever(e.target.checked)}
                    className="h-4 w-4 mt-0.5 accent-amber-600"
                  />
                  <span className="font-semibold text-amber-900">Sim, quero sobrescrever — eu compreendo que a contagem será somada</span>
                </label>
              </div>
            )}

            {/* Lista de operações agrupadas por categoria */}
            <div>
              <p className="text-xs font-semibold text-stone-600 mb-2">
                Escolher operação alvo ({data.items.length} no mesmo lote · {gruposCategoria.length} categoria(s))
              </p>
              {data.items.length === 0 ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  Não há outras operações disponíveis no mesmo lote desta OF.
                </div>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto pr-1">
                  {gruposCategoria.map((grupo) => {
                    const isCatAtual = grupo.categoria_id === (opAtual.categoria_id || '__sem__');
                    return (
                      <div key={grupo.categoria_id} data-testid={`trocar-cat-${grupo.categoria_id}`}>
                        <div
                          className="sticky top-0 bg-white z-10 flex items-center gap-2 pb-1 mb-1.5 border-b-2"
                          style={{ borderColor: grupo.categoria_cor || '#e2e8f0' }}
                        >
                          <span
                            className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                            style={{ background: grupo.categoria_cor || '#94a3b8' }}
                          />
                          <span className="text-[11px] uppercase font-extrabold tracking-wider" style={{ color: grupo.categoria_cor || '#475569' }}>
                            {grupo.categoria_nome}
                          </span>
                          {isCatAtual && (
                            <span
                              className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase text-white"
                              style={{ background: grupo.categoria_cor || '#475569' }}
                            >
                              categoria actual
                            </span>
                          )}
                          <span className="text-[10px] text-stone-400 ml-auto">{grupo.items.length} op.</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {grupo.items.map((op) => {
                            const sel = op.operacao_id === novaOpId;
                            const bloqueado = !op.elegivel;
                            const temPecas = op.quantidade_ja_produzida > 0;
                            return (
                              <button
                                key={op.operacao_id}
                                type="button"
                                disabled={bloqueado}
                                onClick={() => setNovaOpId(op.operacao_id)}
                                data-testid={`trocar-op-${op.operacao_codigo}`}
                                className={`text-left flex items-start gap-2 p-2.5 rounded-lg border-l-4 border-2 transition ${
                                  sel
                                    ? 'shadow-sm'
                                    : bloqueado
                                    ? 'border-stone-200 bg-stone-50 opacity-60 cursor-not-allowed'
                                    : 'bg-white hover:shadow-sm'
                                }`}
                                style={{
                                  borderLeftColor: grupo.categoria_cor || '#94a3b8',
                                  borderColor: sel ? grupo.categoria_cor : (bloqueado ? '#e2e8f0' : '#e7e5e4'),
                                  background: sel ? `${grupo.categoria_cor}15` : undefined,
                                }}
                              >
                                <span
                                  className={`mt-0.5 inline-flex items-center justify-center h-5 w-5 rounded-full border-2 flex-shrink-0`}
                                  style={
                                    sel
                                      ? { background: grupo.categoria_cor || '#047857', borderColor: grupo.categoria_cor || '#047857', color: 'white' }
                                      : { borderColor: '#cbd5e1' }
                                  }
                                >
                                  {sel && <CheckCircle2 className="h-3.5 w-3.5" />}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-baseline gap-1.5 flex-wrap">
                                    <span
                                      className="inline-block px-1.5 py-0 rounded font-bold text-[12px] text-white"
                                      style={{ background: grupo.categoria_cor || '#475569' }}
                                    >
                                      {op.operacao_codigo}
                                    </span>
                                    <span className="text-sm font-medium text-stone-800 truncate">{op.operacao_nome}</span>
                                  </div>
                                  <div className="mt-1 flex items-center gap-2 text-[10px] text-stone-500 flex-wrap">
                                    <span className="inline-flex items-center gap-0.5" title="Tempo standard previsto por peça (em segundos)">
                                      <Clock className="h-2.5 w-2.5" />
                                      {op.tempo_standard && op.tempo_standard > 0
                                        ? `${fmtTempo(op.tempo_standard)}/peça`
                                        : 'sem tempo previsto'}
                                    </span>
                                    {temPecas && (
                                      <span className="inline-flex items-center gap-0.5 text-amber-700 font-semibold" title="Atenção: já tem peças contabilizadas">
                                        <AlertTriangle className="h-2.5 w-2.5" />
                                        {op.quantidade_ja_produzida} pç já feitas
                                      </span>
                                    )}
                                    {bloqueado && (
                                      <span className="inline-flex items-center gap-0.5 text-red-600 font-semibold">
                                        <Lock className="h-2.5 w-2.5" /> em curso por {op.em_curso_por}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="trocar-op-cancelar">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmar}
            disabled={submitting || !novaOpId || (novaOpTemPecas && !aceitouSobrescrever)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="trocar-op-confirmar"
          >
            {submitting ? 'A trocar...' : <><RefreshCw className="h-4 w-4 mr-1" /> Confirmar troca</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
